import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, ConfirmDialog, DataGrid, Modal, ShareMenu, StatusBadge, useToast,
  type DataGridColumn,
} from '@kss/ui';
import { Menu } from '@mantine/core';
import { Plus, RefreshCw, Share2, Trash2, Pencil, Printer, ChevronDown, CheckCircle2, Truck, PackageCheck, XCircle, Eye, Calendar } from 'lucide-react';
import { StandardAddDropdown } from '../shared/AddDropdown';
import type { SalesOrder, SalesOrderItem } from '@kss/api';
import { salesOrderService, enumLabel } from '@kss/api';
import { useModulePerms } from '../../hooks/useModulePerms';
import { encodeId } from '../../utils/idCodec';
import { isUnreachableHost, orderUrl } from '../../utils/storefrontLinks';
import { NameCell } from '../../hooks/useNameMaps';

// Sales-order lifecycle enum (mirrors packages/api SalesOrderStatusLabels).
// Kept as a local const so this file can reason about transitions without
// importing the whole label map — see `nextStatusActions` below.
const STATUS_DRAFT = 1;
const STATUS_CONFIRMED = 2;
const STATUS_SHIPPED = 3;
const STATUS_DELIVERED = 4;
const STATUS_CANCELLED = 5;

export default function SalesOrders() {
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast);
  toastRef.current = toast;

  const perms = useModulePerms('SalesOrder');
  const [rows, setRows] = useState<SalesOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<(string | number)[]>([]);
  const [detailRow, setDetailRow] = useState<SalesOrder | null>(null);
  /* Share opens a modal rather than an inline menu — a dropdown inside a
     grid cell is clipped by the row, and the modal previews the exact link. */
  const [shareRow, setShareRow] = useState<SalesOrder | null>(null);
  // Full hydrated order (with line items) fetched via .get() when a row's
  // preview modal is opened. The grid's `salesOrderService.all()` response
  // does not reliably include SalesOrderItem[] on every backend build, so
  // the preview modal fetches its own copy to guarantee items render.
  const [detailFull, setDetailFull] = useState<SalesOrder | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState<SalesOrder | null>(null);
  const [deleting, setDeleting] = useState(false);
  // Status transitions — Cancel needs a confirm dialog; the forward
  // moves (Confirm / Ship / Deliver) fire directly from the menu. We
  // also track which row is busy so its menu can visually disable
  // while the PUT is in flight, avoiding double-clicks that would
  // resurrect a stale status.
  const [confirmCancel, setConfirmCancel] = useState<SalesOrder | null>(null);
  const [statusBusyId, setStatusBusyId] = useState<string | null>(null);

  // SSO Issue #10 — date-range filter (client-side). Two committed
  // `applied*` bounds drive the actual filter so typing in the inputs
  // doesn't reflow the grid on every keystroke; the "Apply" button and
  // the quick-select chips are the only ways to commit new bounds.
  const [dateFromDraft, setDateFromDraft] = useState<string>('');
  const [dateToDraft, setDateToDraft] = useState<string>('');
  const [dateFromApplied, setDateFromApplied] = useState<string>('');
  const [dateToApplied, setDateToApplied] = useState<string>('');
  const [activeChip, setActiveChip] = useState<
    'today' | 'week' | 'month' | 'lastMonth' | null
  >(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const data = await salesOrderService.all();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Failed to load sales orders';
      setError(msg); toastRef.current.error('Load failed', msg);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Hydrate the full order (with line items) whenever the preview
  // modal opens. If the row already carries items (some builds of
  // .all() include them) we skip the fetch to avoid a redundant round
  // trip. Failures fall back to whatever we have on the summary row —
  // header details still render, items section shows a warning.
  useEffect(() => {
    if (!detailRow?.salesOrderId) {
      setDetailFull(null);
      return;
    }
    if (Array.isArray(detailRow.items) && detailRow.items.length > 0) {
      setDetailFull(detailRow);
      return;
    }
    let cancelled = false;
    setDetailLoading(true);
    setDetailFull(null);
    (async () => {
      try {
        const full = await salesOrderService.get(detailRow.salesOrderId!);
        if (!cancelled) setDetailFull(full);
      } catch {
        if (!cancelled) setDetailFull(detailRow);
      } finally {
        if (!cancelled) setDetailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [detailRow]);

  // Client-side date filter — applied bounds gate the visible rows.
  // We compare against the row's `orderDate` (ISO or YYYY-MM-DD) after
  // slicing to the day, so a From/To of the same day still matches
  // orders stamped mid-day. Rows without an orderDate always pass
  // through so admins don't lose them on a filter mishap.
  const filteredRows = useMemo(() => {
    if (!dateFromApplied && !dateToApplied) return rows;
    return rows.filter((r) => {
      const raw = r.orderDate;
      if (!raw) return true;
      const day = String(raw).slice(0, 10);
      if (dateFromApplied && day < dateFromApplied) return false;
      if (dateToApplied && day > dateToApplied) return false;
      return true;
    });
  }, [rows, dateFromApplied, dateToApplied]);

  const applyDateRange = (from: string, to: string, chip: typeof activeChip = null) => {
    setDateFromDraft(from);
    setDateToDraft(to);
    setDateFromApplied(from);
    setDateToApplied(to);
    setActiveChip(chip);
  };
  const clearDateRange = () => applyDateRange('', '', null);
  const applyQuickRange = (kind: 'today' | 'week' | 'month' | 'lastMonth') => {
    const range = computeQuickRange(kind);
    applyDateRange(range.from, range.to, kind);
  };

  const handleDelete = async () => {
    if (!confirmDelete?.salesOrderId) return;
    setDeleting(true);
    try {
      await salesOrderService.delete(confirmDelete.salesOrderId);
      toast.success('Deleted', `Order removed`);
      setConfirmDelete(null); await load();
    } catch (e) { toast.error('Delete failed', e instanceof Error ? e.message : 'Failed'); }
    finally { setDeleting(false); }
  };

  /**
   * Advance / cancel an order via PUT. We spread the row so every
   * other column (customer, totals, address JSON, …) round-trips
   * intact — the strict-mode binder rejects a PUT with a partial
   * payload as `UNKNOWN_FIELD`. When moving to Shipped we also stamp
   * `shippedAt` (column added in migration 20260629160000) so SLA
   * reports have a concrete ship timestamp instead of falling back to
   * `updatedAt`.
   */
  const changeStatus = async (row: SalesOrder, next: number) => {
    if (!row.salesOrderId) return;
    setStatusBusyId(row.salesOrderId);
    try {
      const payload: SalesOrder = { ...row, status: next };
      if (next === STATUS_SHIPPED && !row.shippedAt) {
        payload.shippedAt = new Date().toISOString();
      }
      await salesOrderService.update([payload]);
      toast.success('Order updated', `${row.orderNumber ?? ''} → ${enumLabel('SalesOrderStatus', next)}`);
      await load();
    } catch (e) {
      toast.error('Update failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setStatusBusyId(null);
    }
  };

  const handleConfirmCancel = async () => {
    if (!confirmCancel) return;
    const row = confirmCancel;
    setConfirmCancel(null);
    await changeStatus(row, STATUS_CANCELLED);
  };

  const columns: DataGridColumn<SalesOrder>[] = [
    {
      accessor: 'orderNumber',
      title: 'Order #',
      sortable: true,
      filterable: true,
      width: 140,
      // Make the cell a clickable link that opens the detail modal
      // (same modal the row click also opens) so admins can scan the
      // grid and jump straight into details without hunting for the
      // exact row click target.
      render: (r) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setDetailRow(r);
          }}
          className="font-medium text-primary underline-offset-2 hover:underline"
        >
          {r.orderNumber ?? '—'}
        </button>
      ),
    },
    { accessor: 'orderDate', title: 'Date', sortable: true, render: (r) => r.orderDate ? new Date(r.orderDate).toLocaleDateString() : '—' },
    { accessor: 'channel', title: 'Channel', sortable: true, filterable: true,
      render: (r) => enumLabel('SalesOrderChannel', toEnumValue(r.channel)) },
    { accessor: 'status', title: 'Status', sortable: true,
      render: (r) => <StatusBadge status={enumLabel('SalesOrderStatus', toEnumValue(r.status))} /> },
    { accessor: 'totalAmount', title: 'Total (₹)', sortable: true, textAlign: 'right',
      render: (r) => Number(r.totalAmount ?? 0).toLocaleString('en-IN') },
    // /api/Customer/all is now live (2026-05-02). Resolve customerId
     // → customer name via NameCell, but prefer the shipping address's
     // `fullName` when present (SSO ISSUES 2 & 3 — web checkouts stamp
     // `customerId = user.id` and never create a `Customer` row, so
     // the raw NameCell lookup always misses on storefront orders).
    { accessor: 'customerId', title: 'Customer', render: (r) => <CustomerCell row={r} /> },
    { accessor: 'salesOrderId', title: 'Actions', width: 190,
      render: (r) => {
        const statusInt = toEnumValue(r.status) ?? 0;
        const busy = !!r.salesOrderId && statusBusyId === r.salesOrderId;
        // Forward-only: Draft → Confirmed → Shipped → Delivered. Cancel
        // is offered from every non-terminal state. Menu items are only
        // rendered when their transition is valid so admins can't
        // accidentally skip a step (e.g. Draft → Delivered) — SLA
        // reports rely on the intermediate timestamps.
        const canConfirm = perms.canUpdate && statusInt === STATUS_DRAFT;
        const canShip = perms.canUpdate && statusInt === STATUS_CONFIRMED;
        const canDeliver = perms.canUpdate && statusInt === STATUS_SHIPPED;
        const canCancel =
          perms.canUpdate &&
          statusInt > 0 &&
          statusInt !== STATUS_DELIVERED &&
          statusInt !== STATUS_CANCELLED;
        const hasStatusAction = canConfirm || canShip || canDeliver || canCancel;
        return (
          <div className="flex items-center gap-1">
            {hasStatusAction && (
              // Client asked for a labeled dropdown ("Actions ▾")
              // instead of the previous 3-dot icon menu — the icon
              // was ambiguous to non-technical admins who couldn't
              // tell the status actions apart from the row-level
              // Edit/Delete icons next to it. See SSO Issue #2.
              <Menu shadow="md" width={180} position="bottom-end" withinPortal>
                <Menu.Target>
                  <button
                    type="button"
                    disabled={busy}
                    className="inline-flex h-7 items-center gap-1 rounded border border-border bg-surface-card px-2 text-xs font-medium text-content-secondary hover:bg-surface-card-hover disabled:opacity-50"
                    onClick={(e) => e.stopPropagation()}
                    title="Status actions"
                  >
                    Actions <ChevronDown className="h-3 w-3" />
                  </button>
                </Menu.Target>
                <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                  {canConfirm && (
                    <Menu.Item leftSection={<CheckCircle2 className="h-3.5 w-3.5" />} onClick={() => changeStatus(r, STATUS_CONFIRMED)}>
                      Confirm
                    </Menu.Item>
                  )}
                  {canShip && (
                    <Menu.Item leftSection={<Truck className="h-3.5 w-3.5" />} onClick={() => changeStatus(r, STATUS_SHIPPED)}>
                      Mark Shipped
                    </Menu.Item>
                  )}
                  {canDeliver && (
                    <Menu.Item leftSection={<PackageCheck className="h-3.5 w-3.5" />} onClick={() => changeStatus(r, STATUS_DELIVERED)}>
                      Mark Delivered
                    </Menu.Item>
                  )}
                  {canCancel && (
                    <Menu.Item
                      color="red"
                      leftSection={<XCircle className="h-3.5 w-3.5" />}
                      onClick={() => setConfirmCancel(r)}
                    >
                      Cancel Order
                    </Menu.Item>
                  )}
                </Menu.Dropdown>
              </Menu>
            )}
            {/* SSO Issue #10 — quick preview opens the read-only detail
                modal with header + line items + grand total, without
                navigating away from the grid. Existing Edit pencil
                below still routes to the full form for editing. */}
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover"
              onClick={(e) => { e.stopPropagation(); setShareRow(r); }}
              title="Share order with customer"
            >
              <Share2 className="h-3.5 w-3.5" />
            </button>
            <button
              type="button"
              className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover"
              onClick={(e) => { e.stopPropagation(); setDetailRow(r); }}
              title="View details"
            >
              <Eye className="h-3.5 w-3.5" />
            </button>
            {perms.canUpdate && (
              <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-content-secondary hover:bg-surface-card-hover"
                onClick={(e) => { e.stopPropagation(); if (r.salesOrderId) navigate(`/panel/sales-orders/${encodeId(r.salesOrderId)}`); }} title="Edit">
                <Pencil className="h-3.5 w-3.5" />
              </button>
            )}
            {perms.canDelete && (
              <button type="button" className="inline-flex h-7 w-7 items-center justify-center rounded text-danger hover:bg-surface-card-hover"
                onClick={(e) => { e.stopPropagation(); setConfirmDelete(r); }} title="Delete">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        );
      } },
  ];

  return (
    <div className="-m-6 p-[3px]">
      {error && <Alert variant="danger" className="mb-3">{error}</Alert>}
      {/* SSO Issue #10 — date-range filter strip. Draft inputs and
          quick chips commit to the applied bounds that drive the grid.
          Client-side filter — the backend endpoint isn't paginated by
          date, so filtering here matches how invoice / expense pages
          scope their toolbars. */}
      <div className="mb-[3px] flex flex-wrap items-end gap-2 rounded-md border border-border bg-surface-card p-3 text-xs">
        <div className="flex items-center gap-1 text-content-secondary">
          <Calendar className="h-3.5 w-3.5" />
          <span className="font-medium">Filter by order date</span>
        </div>
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px] uppercase tracking-wide text-content-muted">From</span>
          <input
            type="date"
            value={dateFromDraft}
            onChange={(e) => setDateFromDraft(e.target.value)}
            className="h-8 rounded border border-border bg-surface-card px-2 text-xs text-content-primary outline-none focus:border-primary"
          />
        </label>
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px] uppercase tracking-wide text-content-muted">To</span>
          <input
            type="date"
            value={dateToDraft}
            onChange={(e) => setDateToDraft(e.target.value)}
            className="h-8 rounded border border-border bg-surface-card px-2 text-xs text-content-primary outline-none focus:border-primary"
          />
        </label>
        <Button
          variant="outline"
          size="sm"
          onClick={() => applyDateRange(dateFromDraft, dateToDraft, null)}
        >
          Apply
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={clearDateRange}
          disabled={!dateFromApplied && !dateToApplied && !dateFromDraft && !dateToDraft}
        >
          Clear
        </Button>
        <div className="mx-1 h-6 w-px bg-border" />
        <QuickChip label="Today" active={activeChip === 'today'} onClick={() => applyQuickRange('today')} />
        <QuickChip label="This week" active={activeChip === 'week'} onClick={() => applyQuickRange('week')} />
        <QuickChip label="This month" active={activeChip === 'month'} onClick={() => applyQuickRange('month')} />
        <QuickChip label="Last month" active={activeChip === 'lastMonth'} onClick={() => applyQuickRange('lastMonth')} />
        {(dateFromApplied || dateToApplied) && (
          <span className="ml-auto text-[11px] text-content-muted">
            Showing <strong className="text-content-primary">{filteredRows.length}</strong> of {rows.length}
            {dateFromApplied && <> · from <strong className="text-content-primary">{dateFromApplied}</strong></>}
            {dateToApplied && <> · to <strong className="text-content-primary">{dateToApplied}</strong></>}
          </span>
        )}
      </div>
      <DataGrid<SalesOrder>
        title={<span className="hidden sm:inline">All sales orders</span>}
        columnPicker onRowClick={(row) => setDetailRow(row)} data={filteredRows} columns={columns}
        selectable selectedIds={selected} onSelectedChange={setSelected}
        defaultPageSize={10} pageSizes={[5, 10, 25, 50, 100]}
        // Chronological grid — newest at the top by default so the order
        // an admin just created is on page 1 rather than buried by
        // orderNumber-ascending sort. See ORDER_WORKFLOW_AUDIT.md Issue 6.
        initialSortStatus={{ columnAccessor: 'orderDate', direction: 'desc' }}
        toolbar={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={load} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <StandardAddDropdown
              basePath="/panel/sales-orders"
              newLabel="New order"
              service={salesOrderService}
              templateFields={['orderNumber', 'orderDate', 'channel', 'status', 'totalAmount', 'customerId', 'notes']}
            />
          </div>
        }
      />
      {/* Share sheet — previews the customer-facing link before it goes out. */}
      <Modal
        open={!!shareRow}
        onClose={() => setShareRow(null)}
        title={shareRow ? `Share order ${shareRow.orderNumber ?? ''}` : ''}
        width={520}
      >
        {shareRow?.salesOrderId && (() => {
          const link = orderUrl(shareRow.salesOrderId);
          const unreachable = isUnreachableHost(link);
          const due = Number(shareRow.balanceAmount ?? 0);
          return (
            <div className="grid gap-3">
              <p className="text-sm text-content-secondary">
                Sends the customer a link to track this order and pay any balance.
              </p>
              <label className="block">
                <span className="mb-1 block text-xs font-medium text-content-secondary">Order link</span>
                <input
                  readOnly
                  value={link}
                  onFocus={(e) => e.currentTarget.select()}
                  className="h-9 w-full rounded-md border border-border bg-surface-card-hover px-2 text-xs text-content-primary"
                />
              </label>
              {unreachable && (
                <Alert variant="warning">
                  This link points at a local address and won't open on anyone else's device.
                  Set <code>window.__KSS_CONFIG__.storefrontOrigin</code> in runtime-config.js.
                </Alert>
              )}
              <div>
                <ShareMenu
                  content={{
                    title: `Order ${shareRow.orderNumber ?? ''}`,
                    message: [
                      `*Order ${shareRow.orderNumber ?? ''}*`,
                      `Total: ₹${Number(shareRow.totalAmount ?? 0).toLocaleString('en-IN')}`,
                      due > 0 ? `Balance due: ₹${due.toLocaleString('en-IN')}` : 'Paid in full',
                    ].join('\n'),
                    url: link,
                  }}
                  label="Send via…"
                  warning={unreachable ? 'Link is not publicly reachable' : null}
                  onResult={(_c, result) => {
                    if (result === 'copied') toastRef.current.success('Link copied');
                    if (result === 'unsupported') toastRef.current.error('Sharing unavailable', 'Try copying the link.');
                  }}
                />
              </div>
            </div>
          );
        })()}
      </Modal>

      <Modal
        open={!!detailRow}
        onClose={() => setDetailRow(null)}
        title={detailRow ? `Order · ${detailRow.orderNumber || ''}` : ''}
        width={720}
        footer={
          <>
            <Button variant="outline" onClick={() => setDetailRow(null)}>Close</Button>
            <Button variant="outline" onClick={() => printInvoice('invoice-print-area')}>
              <Printer className="h-4 w-4" /> Print invoice
            </Button>
            {detailRow?.salesOrderId && (
              <Button
                onClick={() => {
                  const id = detailRow.salesOrderId!;
                  setDetailRow(null);
                  navigate(`/panel/sales-orders/${encodeId(id)}`);
                }}
              >
                Edit
              </Button>
            )}
          </>
        }
      >
        {detailRow && (
          // Wrapped in a printable area so the Print button targets a
          // self-contained invoice render rather than the entire panel
          // shell. See `printInvoice` below — it opens a new window
          // with just this DOM node + a minimal stylesheet.
          // SSO Issue #10 — the modal is READ-ONLY: no inline editing.
          // Line items + grand total come from `detailFull` (hydrated
          // via .get()) so the panel-level preview matches what the
          // full form would show.
          <div id="invoice-print-area" className="grid gap-3 rounded-md border border-border bg-surface-card-hover p-4 text-sm">
            <div className="flex items-start justify-between border-b border-border pb-2">
              <div>
                <div className="text-base font-semibold text-content-primary">
                  Invoice for Order {detailRow.orderNumber || '—'}
                </div>
                <div className="text-xs text-content-muted">
                  {detailRow.orderDate
                    ? new Date(detailRow.orderDate).toLocaleDateString()
                    : ''}
                </div>
              </div>
              <StatusBadge
                status={enumLabel('SalesOrderStatus', toEnumValue(detailRow.status))}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <DetailRow label="Channel" value={enumLabel('SalesOrderChannel', toEnumValue(detailRow.channel))} />
              <DetailRow label="Total" value={`₹ ${Number(detailRow.totalAmount ?? 0).toLocaleString('en-IN')}`} />
            </div>
            <DetailRow
              label="Customer"
              // Resolve `customerId` to the customer's display name —
              // matches what the grid column shows, instead of a
              // truncated UUID that admins would have to look up. Uses
              // the same shipping-address / NameCell / "Guest customer"
              // fallback chain as the list column.
              value={<CustomerCell row={detailRow} />}
            />
            <DetailRow label="Notes" value={detailRow.notes} />

            {/* Line items — hydrated from salesOrderService.get(id).
                Falls back to whatever the summary row has if the fetch
                failed. Renders read-only (no qty/price edits). */}
            <OrderItemsPreview
              order={detailFull ?? detailRow}
              loading={detailLoading}
            />

            <div className="mt-2 border-t border-border pt-2 text-right text-xs text-content-muted">
              Generated by KSS Mart · {new Date().toLocaleString()}
            </div>
          </div>
        )}
      </Modal>
      <ConfirmDialog isOpen={!!confirmDelete} onClose={() => (deleting ? undefined : setConfirmDelete(null))}
        onConfirm={handleDelete} title="Delete sales order"
        message={confirmDelete ? `Remove order ${confirmDelete.orderNumber || ''}? This cannot be undone.` : ''}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'} variant="danger" />
      <ConfirmDialog isOpen={!!confirmCancel} onClose={() => setConfirmCancel(null)}
        onConfirm={handleConfirmCancel} title="Cancel Order?"
        message={confirmCancel ? `Cancel order ${confirmCancel.orderNumber || ''}? The customer will no longer be able to modify it and the status will move to Cancelled.` : ''}
        confirmLabel="Cancel Order" cancelLabel="Keep Order" variant="danger" />
    </div>
  );
}

/**
 * Coerce a SalesOrder enum field (`channel`, `status`, …) to a real
 * integer for `enumLabel`. The API ships ints, but legacy mocks +
 * old rows occasionally have strings — and `Number(undefined)` is
 * `NaN`, which slips past `enumLabel`'s `== null` guard and renders
 * "Sales Order Channel #NaN" on screen. This helper normalises both.
 */
function toEnumValue(v: unknown): number | undefined {
  if (v == null) return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}

/**
 * SSO ISSUES 2 & 3 — resolve a SalesOrder's customer display name.
 *
 * Web checkouts stamp `SalesOrder.customerId = user.id` (see
 * `apps/web/src/shop/CheckoutPage.tsx`), which never matches any row
 * returned by `/api/Customer/all` — so the plain NameCell lookup
 * always missed, leaving the grid's Customer column blank.
 *
 * Fallback chain (best → worst):
 *   1. Denormalised `customerName` / `customer.name` if the backend
 *      ever ships them (defensive — the type doesn't declare them but
 *      the DTO could add them later without a frontend rebuild).
 *   2. `shippingAddressJson.fullName` — this is what web checkout
 *      actually captures, so storefront orders always resolve here.
 *   3. `NameCell kind="customer"` — for orders created inside the
 *      panel (which DO reference a real Customer row).
 *   4. "Guest customer" — final muted-tone fallback so the cell is
 *      never blank.
 */
function CustomerCell({ row }: { row: SalesOrder }): React.ReactElement {
  const inline = readInlineCustomerName(row);
  if (inline) return <span className="text-sm">{inline}</span>;
  return <NameCell kind="customer" id={row.customerId} fallback="Guest customer" />;
}

function readInlineCustomerName(row: SalesOrder): string | null {
  // The live SalesOrder DTO does not declare `customerName` or a
  // nested `customer` object, but the strict-mode binder doesn't
  // strip extras on the response side — the backend team may add
  // either without touching frontend types. Probe both defensively.
  const rec = row as unknown as Record<string, unknown>;
  const denorm = rec.customerName;
  if (typeof denorm === 'string' && denorm.trim()) return denorm.trim();
  const nested = rec.customer;
  if (nested && typeof nested === 'object') {
    const name = (nested as Record<string, unknown>).name;
    if (typeof name === 'string' && name.trim()) return name.trim();
  }
  const raw = row.shippingAddressJson;
  if (raw && typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw) as Record<string, unknown>;
      const full = parsed?.fullName;
      if (typeof full === 'string' && full.trim()) return full.trim();
    } catch {
      /* Not JSON — the field is optional and legacy rows may hold a
         plain string. Skip silently and fall through to NameCell. */
    }
  }
  return null;
}

/**
 * Small pill button used by the date-filter quick chips (Today / This
 * week / This month / Last month). Renders active state as a filled
 * primary pill; idle state as a bordered card.
 */
function QuickChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={
        active
          ? 'inline-flex h-7 items-center rounded-full bg-primary px-3 text-[11px] font-medium text-white'
          : 'inline-flex h-7 items-center rounded-full border border-border bg-surface-card px-3 text-[11px] font-medium text-content-secondary hover:bg-surface-card-hover'
      }
    >
      {label}
    </button>
  );
}

/**
 * Compute a YYYY-MM-DD `from` / `to` range for the quick chips.
 * "This week" starts on Monday. All bounds are inclusive.
 */
function computeQuickRange(kind: 'today' | 'week' | 'month' | 'lastMonth'): {
  from: string;
  to: string;
} {
  const now = new Date();
  const iso = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };
  if (kind === 'today') {
    const s = iso(now);
    return { from: s, to: s };
  }
  if (kind === 'week') {
    const d = new Date(now);
    // JS: Sun=0..Sat=6 — shift so Mon=0..Sun=6 for a Mon-based week.
    const dow = (d.getDay() + 6) % 7;
    d.setDate(d.getDate() - dow);
    return { from: iso(d), to: iso(now) };
  }
  if (kind === 'month') {
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    return { from: iso(start), to: iso(now) };
  }
  // lastMonth — 1st..last day of the previous calendar month
  const start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const end = new Date(now.getFullYear(), now.getMonth(), 0);
  return { from: iso(start), to: iso(end) };
}

/**
 * Read-only line-items block for the preview modal. Uses `NameCell`
 * to resolve `productId` to the product name so admins never see raw
 * UUIDs. Grand total is recomputed client-side from the lines so a
 * stale header total (rare, but possible for legacy rows) is
 * corrected in the preview.
 */
function OrderItemsPreview({
  order,
  loading,
}: {
  order: SalesOrder;
  loading: boolean;
}) {
  const items: SalesOrderItem[] = Array.isArray(order.items) ? order.items : [];
  const grandTotal = items.reduce(
    (acc, i) => acc + Number(i.total ?? Number(i.quantity ?? 0) * Number(i.unitPrice ?? 0)),
    0,
  );
  return (
    <div className="rounded border border-border bg-surface-card">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <span className="text-xs font-semibold uppercase tracking-wide text-content-secondary">
          Line items
        </span>
        <span className="text-[11px] text-content-muted">
          {loading ? 'Loading…' : `${items.length} item(s)`}
        </span>
      </div>
      {loading && items.length === 0 ? (
        <div className="px-3 py-4 text-center text-xs text-content-muted">Loading items…</div>
      ) : items.length === 0 ? (
        <div className="px-3 py-4 text-center text-xs text-content-muted">
          No line items on this order.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-surface-card-hover text-[11px] uppercase text-content-muted">
              <tr>
                <th className="w-[46%] px-3 py-2 text-left">Product</th>
                <th className="w-[12%] px-3 py-2 text-right">Qty</th>
                <th className="w-[18%] px-3 py-2 text-right">Unit price (₹)</th>
                <th className="w-[24%] px-3 py-2 text-right">Amount (₹)</th>
              </tr>
            </thead>
            <tbody>
              {items.map((i, idx) => {
                const qty = Number(i.quantity ?? 0);
                const unit = Number(i.unitPrice ?? 0);
                const amount = Number(i.total ?? qty * unit);
                return (
                  <tr
                    key={i.salesOrderItemId ?? `${i.productId}_${idx}`}
                    className="border-t border-border align-top"
                  >
                    <td className="px-3 py-2 text-content-primary">
                      <NameCell kind="product" id={i.productId} fallback="—" />
                    </td>
                    <td className="px-3 py-2 text-right">{qty.toLocaleString('en-IN')}</td>
                    <td className="px-3 py-2 text-right">{unit.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</td>
                    <td className="px-3 py-2 text-right font-medium text-content-primary">
                      {amount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="border-t border-border bg-surface-card-hover">
                <td colSpan={3} className="px-3 py-2 text-right text-xs font-medium text-content-secondary">
                  Grand total
                </td>
                <td className="px-3 py-2 text-right text-sm font-semibold text-content-primary">
                  ₹ {grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded border border-border bg-surface-card px-3 py-2">
      <span className="text-xs text-content-muted">{label}</span>
      <span className="text-sm font-medium text-content-primary">{value || <em className="text-content-muted">—</em>}</span>
    </div>
  );
}

/**
 * Pop a clean print-window for the invoice DOM node. We can't just call
 * `window.print()` from the panel because that would print the whole
 * sidebar / toolbar / detail modal frame — not the invoice. Cloning
 * the node into a fresh window keeps the print stylesheet minimal and
 * predictable across browsers.
 */
function printInvoice(elementId: string): void {
  const node = document.getElementById(elementId);
  if (!node) return;
  const w = window.open('', '_blank', 'width=720,height=900');
  if (!w) return;
  w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8" /><title>Invoice</title>
    <style>
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; padding: 32px; color: #111; }
      .row { display: flex; justify-content: space-between; padding: 8px 12px; border: 1px solid #d4d4d8; border-radius: 4px; margin-bottom: 8px; }
      .row span:first-child { color: #71717a; font-size: 12px; }
      .row span:last-child  { font-weight: 600; }
      h1 { font-size: 20px; margin: 0 0 16px; }
      .pad { padding: 0; border: 0; background: transparent; }
      .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 8px; }
    </style>
    </head><body>${node.outerHTML.replace(/class="[^"]*"/g, '').replace(/<button[\s\S]*?<\/button>/g, '')}</body></html>`);
  w.document.close();
  w.focus();
  // Slight delay so the print dialog gets the rendered content.
  setTimeout(() => {
    w.print();
  }, 250);
}
